/**
 * Deckbuilder - Parse deck lists and find best prices from inventory
 */

(function() {
  'use strict';

  const DeckBuilder = {
    elements: {},
    deckItems: [],
    foundProducts: [],
    selectedGameType: '',

    init() {
      this.cacheElements();
      this.bindEvents();
      this.loadFromLocalStorage();
    },

    cacheElements() {
      this.elements = {
        gameSelect: document.getElementById('game-select'),
        textarea: document.getElementById('deck-list-input'),
        parseBtn: document.getElementById('parse-deck-btn'),
        clearBtn: document.getElementById('clear-deck-btn'),
        resultsArea: document.getElementById('deck-results'),
        cardsList: document.getElementById('cards-list'),
        progressBar: document.getElementById('search-progress'),
        progressText: document.getElementById('progress-text'),
        summary: document.getElementById('deck-summary'),
        statFound: document.getElementById('stat-found'),
        statMissing: document.getElementById('stat-missing'),
        statTotal: document.getElementById('stat-total'),
        addAllBtn: document.getElementById('add-all-btn')
      };
    },

    bindEvents() {
      this.elements.parseBtn?.addEventListener('click', () => this.parseDeckList());
      this.elements.clearBtn?.addEventListener('click', () => this.clearAll());
      this.elements.addAllBtn?.addEventListener('click', () => this.addAllToCart());
      this.elements.textarea?.addEventListener('input', () => this.saveToLocalStorage());
    },

    /**
     * Parse the deck list text into structured data
     */
    parseDeckList() {
      // Check game is selected
      this.selectedGameType = this.elements.gameSelect?.value || '';
      if (!this.selectedGameType) {
        this.showToast('error', 'Please select a game first!');
        this.elements.gameSelect?.focus();
        return;
      }

      const text = this.elements.textarea.value.trim();
      if (!text) return;

      this.deckItems = [];
      this.foundProducts = [];
      
      const lines = text.split('\n').filter(line => line.trim());
      
      for (const line of lines) {
        const parsed = this.parseLine(line.trim());
        if (parsed) {
          this.deckItems.push(parsed);
        }
      }

      if (this.deckItems.length > 0) {
        this.showResults();
        this.searchAllCards();
      }
    },

    /**
     * Parse a single line like "4 x Charizard ex" or "4 Charizard ex"
     */
    parseLine(line) {
      // Match patterns like: "4 x Card Name", "4x Card Name", "4 Card Name"
      const match = line.match(/^(\d+)\s*[xX]?\s*(.+)$/);
      if (match) {
        return {
          quantity: parseInt(match[1], 10),
          name: match[2].trim(),
          status: 'pending',
          matches: [],
          totalPrice: 0,
          foundQty: 0
        };
      }
      return null;
    },

    /**
     * Show the results area and render initial card items
     */
    showResults() {
      this.elements.resultsArea.style.display = 'block';
      this.elements.summary.style.display = 'none';
      this.elements.cardsList.innerHTML = '';

      this.deckItems.forEach((item, index) => {
        const el = this.createCardElement(item, index);
        this.elements.cardsList.appendChild(el);
      });

      this.elements.parseBtn.classList.add('is-loading');
    },

    /**
     * Create a card item element
     */
    createCardElement(item, index) {
      const div = document.createElement('div');
      div.className = 'deckbuilder__card-item deckbuilder__card-item--searching';
      div.id = `deck-card-${index}`;
      div.style.animationDelay = `${index * 0.05}s`;
      
      div.innerHTML = `
        <span class="deckbuilder__card-qty">${item.quantity}x</span>
        <span class="deckbuilder__card-name">${this.escapeHtml(item.name)}</span>
        <span class="deckbuilder__card-status deckbuilder__card-status--searching">
          <svg class="deckbuilder__spinner" width="16" height="16" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" stroke-dasharray="32" stroke-linecap="round"/></svg>
          Searching...
        </span>
        <span class="deckbuilder__card-price">-</span>
        <div class="deckbuilder__card-details"></div>
      `;
      
      return div;
    },

    /**
     * Search for all cards sequentially
     */
    async searchAllCards() {
      const total = this.deckItems.length;
      let completed = 0;
      // Base delay between requests to respect rate limits
      const BASE_DELAY_MS = 150;

      for (let i = 0; i < this.deckItems.length; i++) {
        let success = false;
        let attempt = 0;
        const MAX_ATTEMPTS = 3;

        while (!success && attempt < MAX_ATTEMPTS) {
          try {
            await this.searchCard(i);
            success = true;
          } catch (error) {
            attempt++;
            if (attempt < MAX_ATTEMPTS) {
              // Exponential backoff: 300ms, 600ms
              await this.delay(BASE_DELAY_MS * Math.pow(2, attempt));
            }
          }
        }
        completed++;
        this.updateProgress(completed, total);
        // Stagger requests to avoid rate limiting
        if (i < this.deckItems.length - 1) {
          await this.delay(BASE_DELAY_MS);
        }
      }

      this.showSummary();
      this.elements.parseBtn.classList.remove('is-loading');
    },

    /**
     * Search for a single card in inventory
     */
    async searchCard(index) {
      const item = this.deckItems[index];

      try {
        const products = await this.searchProducts(item.name);
        await this.processSearchResults(index, products);
      } catch (error) {
        console.error(`Error searching for ${item.name}:`, error);
        this.markCardAsMissing(index);
      }
    },

    /**
     * Search products using Shopify's search
     */
    async searchProducts(query) {
      try {
        // Try predictive search first
        const response = await this.fetchWithTimeout(
          `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=20&resources[options][unavailable_products]=last`
        );
        const data = await response.json();

        let products = data.resources?.results?.products || [];

        // If no results, try the search page scrape method
        if (products.length === 0) {
          products = await this.scrapeSearchResults(query);
        }

        return products;
      } catch (error) {
        console.error('Search error:', error);
        // Fallback to scrape method
        return await this.scrapeSearchResults(query);
      }
    },

    /**
     * Fallback: Scrape search results page for product handles
     */
    async scrapeSearchResults(query) {
      try {
        const response = await this.fetchWithTimeout(`/search?q=${encodeURIComponent(query)}&type=product&options[prefix]=last`);
        const html = await response.text();

        // Parse product handles from the HTML
        const handleMatches = html.matchAll(/\/products\/([a-zA-Z0-9\-_]+)/g);
        const handles = [...new Set([...handleMatches].map(m => m[1]))];

        // Convert handles to product objects (we'll fetch full details later)
        return handles.slice(0, 20).map(handle => ({ handle }));
      } catch (error) {
        console.error('Scrape search error:', error);
        return [];
      }
    },

    /**
     * Process search results and find best prices
     */
    async processSearchResults(index, products) {
      const item = this.deckItems[index];

      if (!products || products.length === 0) {
        this.markCardAsMissing(index);
        return;
      }

      // Get full product data with variants for accurate inventory
      const detailedProducts = await this.getProductDetails(products);

      // Filter by selected game product type, then score and sort
      const scoredProducts = detailedProducts
        .filter(p => p.available && p.type === this.selectedGameType)
        .map(p => ({
          ...p,
          relevanceScore: this.getTitleMatchScore(item.name, p.title),
          lowestPrice: Math.min(...p.variants.filter(v => v.available).map(v => parseFloat(v.price)) || [Infinity])
        }))
        .filter(p => p.relevanceScore > 0) // Only keep products with some relevance
        .sort((a, b) => {
          // Sort by relevance first (higher is better)
          if (b.relevanceScore !== a.relevanceScore) {
            return b.relevanceScore - a.relevanceScore;
          }
          // Then by price (lower is better)
          return a.lowestPrice - b.lowestPrice;
        });

      const sortedProducts = scoredProducts;

      if (sortedProducts.length === 0) {
        this.markCardAsMissing(index);
        return;
      }

      // Allocate quantity across products
      let remainingQty = item.quantity;
      let totalPrice = 0;
      const matches = [];

      for (const product of sortedProducts) {
        if (remainingQty <= 0) break;

        // Find the cheapest available variant
        // Only include variants where available is true
        const availableVariants = product.variants
          .filter(v => v.available === true)
          .sort((a, b) => parseFloat(a.price) - parseFloat(b.price));

        for (const variant of availableVariants) {
          if (remainingQty <= 0) break;

          // Shopify's .js API doesn't expose inventory_quantity
          // If variant.available is true, we can add it to cart
          // Take 1 per variant to be safe, unless inventory_quantity is explicitly set
          // Shopify's .js API may return null for inventory_quantity.
          // When unknown, conservatively take only 1 to avoid overselling.
          const stockQty = (typeof variant.inventory_quantity === 'number' && variant.inventory_quantity !== null && variant.inventory_quantity > 0)
            ? variant.inventory_quantity
            : 1;

          const canTake = Math.min(remainingQty, stockQty);
          const price = parseFloat(variant.price);

          matches.push({
            productId: product.id,
            variantId: variant.id,
            title: product.title,
            variantTitle: variant.title !== 'Default Title' ? variant.title : null,
            quantity: canTake,
            price: price,
            totalPrice: price * canTake,
            image: product.featured_image?.url || product.images?.[0]
          });

          totalPrice += price * canTake;
          remainingQty -= canTake;
        }
      }

      item.matches = matches;
      item.totalPrice = totalPrice;
      item.foundQty = item.quantity - remainingQty;

      if (remainingQty === 0) {
        item.status = 'found';
        this.updateCardElement(index, 'found');
      } else if (matches.length > 0) {
        item.status = 'partial';
        this.updateCardElement(index, 'partial');
      } else {
        this.markCardAsMissing(index);
      }

      // Store for add to cart
      this.foundProducts = this.foundProducts.concat(matches);
    },

    /**
     * Get detailed product info including variants
     */
    async getProductDetails(products) {
      const detailed = [];

      for (const product of products) {
        try {
          if (!product.handle) {
            console.warn('Product missing handle:', product);
            continue;
          }
          const response = await this.fetchWithTimeout(`/products/${product.handle}.js`);
          if (!response.ok) {
            console.warn(`Failed to fetch product ${product.handle}:`, response.status);
            continue;
          }
          const data = await response.json();
          if (data && data.variants) {
            detailed.push(data);
          }
        } catch (e) {
          console.warn('Error fetching product details:', e);
        }
      }

      return detailed;
    },

    /**
     * Calculate how well a product title matches the search query
     * Returns a score from 0 (no match) to 100 (perfect match)
     */
    getTitleMatchScore(query, title) {
      const q = query.toLowerCase().trim();
      const t = title.toLowerCase().trim();

      // Extract just the card name from title (remove set info in brackets/parentheses)
      const cardName = t.replace(/\s*[\[\(].*?[\]\)]\s*/g, '').trim();

      // Perfect match
      if (cardName === q) return 100;
      if (t === q) return 100;

      // Card name starts with query
      if (cardName.startsWith(q)) return 90;

      // Title starts with query
      if (t.startsWith(q)) return 85;

      // Card name contains exact query
      if (cardName.includes(q)) return 80;

      // Title contains exact query
      if (t.includes(q)) return 75;

      // Check word-by-word matching
      const queryWords = q.split(/\s+/).filter(w => w.length > 1);
      const titleWords = cardName.split(/\s+/);

      if (queryWords.length === 0) return 0;

      let matchedWords = 0;
      for (const qWord of queryWords) {
        if (titleWords.some(tWord => tWord.includes(qWord) || qWord.includes(tWord))) {
          matchedWords++;
        }
      }

      const wordMatchRatio = matchedWords / queryWords.length;

      // All query words found
      if (wordMatchRatio === 1) return 70;

      // Most query words found
      if (wordMatchRatio >= 0.75) return 50;

      // Some query words found
      if (wordMatchRatio >= 0.5) return 30;

      // Few words found
      if (matchedWords > 0) return 10;

      return 0;
    },

    /**
     * Update a card element with search results
     */
    updateCardElement(index, status) {
      const item = this.deckItems[index];
      const el = document.getElementById(`deck-card-${index}`);
      if (!el) return;

      el.className = `deckbuilder__card-item deckbuilder__card-item--${status}`;

      const statusEl = el.querySelector('.deckbuilder__card-status');
      const priceEl = el.querySelector('.deckbuilder__card-price');
      const detailsEl = el.querySelector('.deckbuilder__card-details');

      if (status === 'found') {
        statusEl.className = 'deckbuilder__card-status deckbuilder__card-status--found';
        statusEl.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg> Found`;
      } else if (status === 'partial') {
        statusEl.className = 'deckbuilder__card-status deckbuilder__card-status--partial';
        statusEl.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg> ${item.foundQty}/${item.quantity}`;
      }

      priceEl.textContent = this.formatMoney(item.totalPrice);

      // Show matched products
      if (item.matches.length > 0) {
        detailsEl.innerHTML = item.matches.map(m => `
          <div class="deckbuilder__card-match">
            <span class="deckbuilder__card-match-name">${this.escapeHtml(m.title)}${m.variantTitle ? ` - ${this.escapeHtml(m.variantTitle)}` : ''}</span>
            <span class="deckbuilder__card-match-qty">×${m.quantity}</span>
            <span class="deckbuilder__card-match-price">${this.formatMoney(m.price)}</span>
          </div>
        `).join('');

        el.classList.add('is-expanded');
      }
    },

    /**
     * Mark a card as missing/not found
     */
    markCardAsMissing(index) {
      const item = this.deckItems[index];
      item.status = 'missing';
      item.foundQty = 0;

      const el = document.getElementById(`deck-card-${index}`);
      if (!el) return;

      el.className = 'deckbuilder__card-item deckbuilder__card-item--missing';

      const statusEl = el.querySelector('.deckbuilder__card-status');
      statusEl.className = 'deckbuilder__card-status deckbuilder__card-status--missing';
      statusEl.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg> Not found`;

      const priceEl = el.querySelector('.deckbuilder__card-price');
      priceEl.textContent = '-';
    },

    /**
     * Update progress bar
     */
    updateProgress(completed, total) {
      const percent = (completed / total) * 100;
      this.elements.progressBar.style.width = `${percent}%`;
      this.elements.progressText.textContent = `Searching... ${completed}/${total} cards`;
    },

    /**
     * Show summary after all searches complete
     */
    showSummary() {
      const found = this.deckItems.filter(i => i.status === 'found').length;
      const partial = this.deckItems.filter(i => i.status === 'partial').length;
      const missing = this.deckItems.filter(i => i.status === 'missing').length;
      const totalPrice = this.deckItems.reduce((sum, item) => sum + item.totalPrice, 0);

      this.elements.statFound.textContent = found + partial;
      this.elements.statMissing.textContent = missing;
      this.elements.statTotal.textContent = this.formatMoney(totalPrice);

      this.elements.summary.style.display = 'block';
      this.elements.progressBar.style.width = '100%';
      this.elements.progressText.textContent = 'Search complete!';

      // Enable add all button if we found anything
      if (this.foundProducts.length > 0) {
        this.elements.addAllBtn.disabled = false;
      }
    },

    /**
     * Add all found products to cart
     */
    async addAllToCart() {
      if (this.foundProducts.length === 0) return;

      this.elements.addAllBtn.classList.add('is-loading');
      this.elements.addAllBtn.disabled = true;

      try {
        // Group by variant ID and sum quantities
        const grouped = {};
        for (const item of this.foundProducts) {
          if (grouped[item.variantId]) {
            grouped[item.variantId].quantity += item.quantity;
          } else {
            grouped[item.variantId] = {
              id: item.variantId,
              quantity: item.quantity
            };
          }
        }

        // Build items array for cart
        const items = Object.values(grouped);

        const response = await this.fetchWithTimeout('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        // Show success message
        this.showToast('success', `Added ${items.length} item(s) to cart!`);

        // Clear found products to free memory after successful add
        this.foundProducts = [];

        // Update cart count bubble
        this.updateCartBubble();

      } catch (error) {
        console.error('Error adding to cart:', error);
        this.showToast('error', 'Error adding items to cart. Please try again.');
      } finally {
        this.elements.addAllBtn.classList.remove('is-loading');
        this.elements.addAllBtn.disabled = false;
      }
    },

    /**
     * Update the cart count bubble
     */
    updateCartBubble() {
      this.fetchWithTimeout('/cart.js')
        .then(res => res.json())
        .then(cart => {
          const cartIcon = document.getElementById('cart-icon-bubble');
          if (!cartIcon) return;

          const bubble = cartIcon.querySelector('.cart-count-bubble');
          const emptyIcon = cartIcon.querySelector('.cart-icon-empty');
          const filledIcon = cartIcon.querySelector('.cart-icon-filled');
          const countEl = bubble?.querySelector('[aria-hidden="true"]');

          if (cart.item_count > 0) {
            if (bubble) bubble.style.display = '';
            if (emptyIcon) emptyIcon.style.display = 'none';
            if (filledIcon) filledIcon.style.display = '';
            if (countEl) countEl.textContent = cart.item_count;
          }
        });
    },

    /**
     * Show a toast notification
     */
    showToast(type, message) {
      // Check if there's an existing toast container
      let container = document.querySelector('.deckbuilder-toast-container');
      if (!container) {
        container = document.createElement('div');
        container.className = 'deckbuilder-toast-container';
        container.style.cssText = 'position:fixed;top:20px;right:20px;z-index:9999;';
        document.body.appendChild(container);
      }

      const toast = document.createElement('div');
      toast.className = `deckbuilder-toast deckbuilder-toast--${type}`;
      toast.style.cssText = `
        padding: 1rem 1.5rem;
        border-radius: 0.5rem;
        margin-bottom: 0.5rem;
        font-size: 1.4rem;
        font-weight: 500;
        animation: deckbuilder-fadeIn 0.3s ease;
        ${type === 'success' ? 'background: #006340; color: #fff;' : 'background: #dc2626; color: #fff;'}
      `;
      toast.textContent = message;
      container.appendChild(toast);

      setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transition = 'opacity 0.3s ease';
        setTimeout(() => toast.remove(), 300);
      }, 3000);
    },

    /**
     * Clear all and reset
     */
    clearAll() {
      this.elements.textarea.value = '';
      this.elements.resultsArea.style.display = 'none';
      this.elements.cardsList.innerHTML = '';
      this.elements.summary.style.display = 'none';
      this.elements.progressBar.style.width = '0%';
      this.elements.addAllBtn.disabled = true;
      this.elements.gameSelect.selectedIndex = 0;
      this.selectedGameType = '';
      this.deckItems = [];
      this.foundProducts = [];
    },

    /**
     * Format money (cents to number)
     */
    formatMoney(cents) {
      return (cents / 100).toFixed(2);
    },

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    /**
     * Delay helper for async operations
     */
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },

    saveToLocalStorage() {
      try {
        localStorage.setItem('tcg_deck_autosave', this.elements.textarea?.value || '');
      } catch (e) {
        // localStorage may be unavailable in private browsing
      }
    },

    loadFromLocalStorage() {
      try {
        const saved = localStorage.getItem('tcg_deck_autosave');
        if (saved && this.elements.textarea) {
          this.elements.textarea.value = saved;
        }
      } catch (e) {
        // localStorage may be unavailable
      }
    },

    fetchWithTimeout(url, options = {}, timeoutMs = 10000) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      return fetch(url, { ...options, signal: controller.signal })
        .finally(() => clearTimeout(timeoutId));
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => DeckBuilder.init());
  } else {
    DeckBuilder.init();
  }
})();

